import React from 'react';
import { ChevronLeft } from 'lucide-react';

/**
 * Shared low-level UI primitives for Global-Search entity modals.
 * Designed to be tiny and stateless so adding new fields/sections
 * later does not require restructuring.
 */

export const DetailSection = ({ title, children, testId }) => (
  <section className="border border-[#E2E4E0] rounded-md bg-white" data-testid={testId}>
    {title && (
      <header className="px-3 py-2 border-b border-[#E2E4E0] bg-[#F8F8F4] text-[10px] uppercase tracking-wider text-[#5C6B8C] font-semibold">
        {title}
      </header>
    )}
    <div className="p-3">{children}</div>
  </section>
);

export const DetailRow = ({ label, value, testId }) => (
  <div className="flex items-start gap-3 py-1" data-testid={testId}>
    <span className="text-[11px] text-[#5C6B8C] min-w-[110px] shrink-0 pt-0.5">{label}</span>
    <span className="text-sm text-[#0A1128] flex-1 break-words">
      {value === null || value === undefined || value === '' ? '—' : value}
    </span>
  </div>
);

/**
 * Clickable chip that opens a nested entity modal.
 * Falls back to a plain span if `onOpen` is not provided.
 */
export const LinkChip = ({ label, sub, kind, id, onOpen, testId }) => {
  const clickable = !!(onOpen && kind && id);
  const Tag = clickable ? 'button' : 'span';
  return (
    <Tag
      type={clickable ? 'button' : undefined}
      onClick={clickable ? () => onOpen(kind, id) : undefined}
      className={
        'inline-flex flex-col items-start text-start gap-0.5 px-2.5 py-1.5 rounded border ' +
        (clickable
          ? 'border-[#0A1128]/15 bg-white hover:bg-[#0A1128] hover:text-[#C9A24E] hover:border-[#0A1128] cursor-pointer transition-colors'
          : 'border-[#E2E4E0] bg-[#F8F8F4] text-[#5C6B8C]')
      }
      data-testid={testId}
    >
      <span className="text-sm font-medium leading-tight">{label || '—'}</span>
      {sub && <span className="text-[10px] opacity-80 leading-tight">{sub}</span>}
    </Tag>
  );
};

export const ChipGrid = ({ children, empty = 'لا توجد عناصر مرتبطة.' }) => {
  const arr = React.Children.toArray(children).filter(Boolean);
  if (arr.length === 0) {
    return <p className="text-xs text-[#5C6B8C]">{empty}</p>;
  }
  return <div className="flex flex-wrap gap-2">{arr}</div>;
};

export const StatusBadge = ({ status }) => {
  if (!status) return <span className="text-[#5C6B8C] text-xs">—</span>;
  const palette = {
    Active:      'bg-emerald-50 text-emerald-700 border-emerald-200',
    Inactive:    'bg-zinc-100 text-zinc-700 border-zinc-200',
    Frozen:      'bg-sky-50 text-sky-700 border-sky-200',
    Closed:      'bg-rose-50 text-rose-700 border-rose-200',
    Pending:     'bg-amber-50 text-amber-700 border-amber-200',
  };
  const cls = palette[status] || 'bg-[#F5F5F0] text-[#0A1128] border-[#E2E4E0]';
  return (
    <span className={`inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full border ${cls}`}>
      {status}
    </span>
  );
};

export const BackButton = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center gap-1 text-[11px] text-[#5C6B8C] hover:text-[#0A1128]"
    data-testid="entity-modal-back"
  >
    <ChevronLeft size={14} />
    عودة
  </button>
);
